export type CurrencyCode = 'USD' | 'EUR'

const STORAGE_KEY = 'agion-currency-preference'

const EXCHANGE_RATES: Record<CurrencyCode, number> = {
  USD: 1,
  EUR: 0.92,
}

const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: '$',
  EUR: '\u20AC',
}

export function convertFromUSD(amount: number, currency: CurrencyCode): number {
  return amount * EXCHANGE_RATES[currency]
}

export function convertToUSD(amount: number, currency: CurrencyCode): number {
  const rate = EXCHANGE_RATES[currency]
  if (!rate) return amount
  return amount / rate
}

export function formatCurrency(amountUSD: number, currency: CurrencyCode): string {
  const converted = convertFromUSD(amountUSD, currency)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(converted)
}

export function formatCurrencyCompact(amountUSD: number, currency: CurrencyCode): string {
  const converted = convertFromUSD(amountUSD, currency)
  const symbol = CURRENCY_SYMBOLS[currency]
  if (converted === 0) return `${symbol}0`
  if (converted >= 1000) return `${symbol}${Math.round(converted / 1000)}K`
  return `${symbol}${Math.round(converted)}`
}

export function formatLocalCompact(amount: number, currency: CurrencyCode): string {
  const symbol = CURRENCY_SYMBOLS[currency]
  if (amount === 0) return `${symbol}0`
  if (amount >= 1000) return `${symbol}${Math.round(amount / 1000)}K`
  return `${symbol}${Math.round(amount)}`
}

export function getCurrencySymbol(currency: CurrencyCode): string {
  return CURRENCY_SYMBOLS[currency]
}

export function getCurrencyPreference(): CurrencyCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'USD' || stored === 'EUR') return stored
  } catch {
    // localStorage unavailable
  }
  return 'EUR'
}

export function setCurrencyPreference(currency: CurrencyCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, currency)
  } catch {
    // localStorage unavailable
  }
}

/**
 * A spend figure as Cost Management shows it (next/pages/platform/cost
 * costFormat.money, pinned equal by currency.test.ts): converted from the
 * stored USD into the display currency, symbol first, two decimals
 * (agion/Agion#2524). Pages outside Cost read this, so one amount reads the
 * same everywhere.
 */
export function formatMoney(amountUSD: number, currency: CurrencyCode): string {
  return getCurrencySymbol(currency) + convertFromUSD(amountUSD, currency).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * An estimated spend: null when nothing was priced (a zero would read as
 * free), "< €0.01" below one cent of the display currency, else formatMoney.
 */
export function formatSpend(amountUSD: number | null | undefined, currency: CurrencyCode): string | null {
  if (amountUSD == null || amountUSD === 0) return null
  if (convertFromUSD(amountUSD, currency) < 0.01) return `< ${getCurrencySymbol(currency)}0.01`
  return formatMoney(amountUSD, currency)
}
